import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ResourceStatus, ResourceType } from '@/lib/shared-types';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { PricingListSelect } from '@/Pages/Pricing/components/PricingListSelect';
import { RefusalNotice } from '@/Pages/Pricing/components/RefusalNotice';
import { PRICING_LIST_FIELD } from '@/Pages/Pricing/pricingRoutes';
import { invalidatePricingAttachment } from '@/Pages/Pricing/pricingListsApi';
import { refusalOf, type Refusal } from '@/Pages/Pricing/lib/refusals';
import { useAppDispatch } from '@/Redux/store';
import type { ResourceRow } from '../lib/sessions';

interface AreaOption {
  id: string;
  name: string;
  isActive: boolean;
  floor?: { id: string; name: string } | null;
}

interface Props {
  open: boolean;
  /** The resource being edited, or null to create one. */
  record: ResourceRow | null;
  onClose: () => void;
  onSaved: () => void;
}

/** Every field the create DTO accepts, as strings, because a form holds strings. */
interface FormState {
  areaId: string;
  type: string;
  name: string;
  code: string;
  capacity: string;
  status: string;
  /** The rate card, or `''` for none — see the note on the picker below. */
  pricingListId: string;
  isActive: boolean;
}

const BLANK: FormState = {
  areaId: '',
  type: ResourceType.PLAYSTATION,
  name: '',
  code: '',
  capacity: '1',
  status: ResourceStatus.AVAILABLE,
  pricingListId: '',
  isActive: true,
};

/**
 * C-16 — the write path for §10 resources.
 *
 * The dashboard read `/resources` in eight places and wrote it in none, so a
 * venue could draw a floor and an area but could not create the PS5 station
 * standing in it: `POST /resources` was the only route to
 * `prisma.resource.create()`, and nothing called it. `resources.create`,
 * `resources.update` and `resources.delete` were in the permission catalogue
 * and enforced on the controller the whole time — granted, gated, and
 * unspendable. This is C-14's shape (floors, areas and tables had the same hole)
 * on the other half of the model.
 *
 * It lives here rather than on a page of its own because Saher folded the old
 * standalone `/resources` screen into the map deliberately — a resource takes
 * food and drink like a table does, so the map is where a manager already is.
 *
 * Scope is why the area picker matters. `POST /resources` carries
 * `@RequireScope(ScopeLevel.AREA, 'areaId')`, so the areas a user may create in
 * are exactly the ones their grant covers; on edit the area is fixed, because
 * moving a resource between areas is a scope change on a row that may have a
 * running session, and `PATCH` is scoped to the RESOURCE rather than the area.
 */
export function ResourceFormDialog({ open, record, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [form, setForm] = useState<FormState>(BLANK);
  const [saving, setSaving] = useState(false);
  /** A local objection — a field this form can refuse before sending anything. */
  const [error, setError] = useState<string | null>(null);
  /**
   * The API's refusal, as a code and its detail keys.
   *
   * Held apart from {@link error} because they are different kinds of thing: one
   * is a sentence this file wrote about a field, the other is a sentence chosen
   * from the locale files by `code`. What is deliberately gone is the third kind
   * — `response.data.message`, printed as it arrived, which is how an Arabic
   * manager was shown an English sentence naming a JSON key they never typed.
   */
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  // Only while the dialog is open: the map behind it does not need this list.
  const areasQuery = useApi<unknown>(open ? '/areas' : null, { limit: 200 });
  const areas = useMemo(() => rowsOf<AreaOption>(areasQuery.data).rows, [areasQuery.data]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setRefusal(null);
    setForm(
      record
        ? {
            areaId: record.areaId,
            type: record.type,
            name: record.name,
            code: record.code ?? '',
            capacity: String(record.capacity),
            status: record.status,
            // The raw column, falling back to the embedded summary: the board's
            // rows carry `pricingList` and the detail carries both, and a picker
            // prefilled from neither would silently detach the card on save.
            pricingListId: record.pricingListId ?? record.pricingList?.id ?? '',
            isActive: record.isActive,
          }
        : BLANK,
    );
  }, [open, record]);

  const set = (patch: Partial<FormState>) => setForm((current) => ({ ...current, ...patch }));

  const save = async () => {
    const capacity = Number(form.capacity);
    if (!form.name.trim()) return setError(t('spaces.resourceForm.nameRequired'));
    if (!record && !form.areaId) return setError(t('spaces.resourceForm.areaRequired'));
    if (!Number.isInteger(capacity) || capacity < 1 || capacity > 500) {
      return setError(t('spaces.resourceForm.capacityRange'));
    }

    setSaving(true);
    setError(null);
    setRefusal(null);
    try {
      // `forbidNonWhitelisted` is on, so only send what the DTO declares — and
      // send `code` as undefined rather than '' so a blank field clears nothing
      // it should not.
      const body = {
        type: form.type,
        name: form.name.trim(),
        code: form.code.trim() || undefined,
        capacity,
        status: form.status,
        // `null`, never omitted, when no card is chosen. A PATCH validates the
        // row it would produce and keeps whatever an absent key held, so
        // detaching a card by clearing the picker would look like it saved and
        // change nothing. The key is a named constant because getting it wrong
        // is a 400 naming a JSON field the manager never typed, and the mistake
        // would otherwise be made twice — once on create and once on edit.
        [PRICING_LIST_FIELD]: form.pricingListId || null,
        isActive: form.isActive,
      };
      if (record) {
        await api.patch(`/resources/${record.id}`, body);
      } else {
        await api.post('/resources', { ...body, areaId: form.areaId });
      }
      // The rate card's own index counts how many resources charge from it, and
      // that count is what tells a manager the tenth console is on nothing.
      dispatch(invalidatePricingAttachment(record?.id));
      toast.success(t(record ? 'spaces.resourceForm.updated' : 'spaces.resourceForm.created'));
      onSaved();
      onClose();
    } catch (err) {
      setRefusal(refusalOf(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t(record ? 'spaces.resourceForm.editTitle' : 'spaces.resourceForm.newTitle')}
      className="max-w-2xl"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field label={t('spaces.resourceForm.area')}>
          {record ? (
            // Fixed on edit — see the note on scope above.
            <Input
              value={record.area?.name ?? record.areaId}
              disabled
              aria-label={t('spaces.resourceForm.area')}
            />
          ) : (
            <Select
              value={form.areaId}
              onChange={(e) => set({ areaId: e.target.value })}
              className="h-12"
            >
              <option value="">{t('spaces.resourceForm.chooseArea')}</option>
              {areas
                .filter((a) => a.isActive)
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.floor?.name ? `${a.floor.name} · ${a.name}` : a.name}
                  </option>
                ))}
            </Select>
          )}
          </Field>
        </div>

        <Field label={t('spaces.resourceForm.type')}>
          <Select
            value={form.type}
            onChange={(e) => set({ type: e.target.value })}
            className="h-12"
          >
            {Object.values(ResourceType).map((value) => (
              <option key={value} value={value}>
                {t(`spaces.resourceType.${value}`)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('spaces.resourceForm.name')}>
          <Input
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder={t('spaces.resourceForm.namePlaceholder')}
            className="h-12"
          />
        </Field>

        <Field label={t('spaces.resourceForm.code')} hint={t('spaces.resourceForm.codeHint')}>
          <Input
            value={form.code}
            onChange={(e) => set({ code: e.target.value })}
            className="h-12"
          />
        </Field>

        <Field
          label={t('spaces.resourceForm.capacity')}
          hint={t('spaces.resourceForm.capacityHint')}
        >
          <Input
            type="number"
            min={1}
            max={500}
            value={form.capacity}
            onChange={(e) => set({ capacity: e.target.value })}
            className="h-12"
          />
        </Field>

        <Field label={t('spaces.resourceForm.status')}>
          <Select
            value={form.status}
            onChange={(e) => set({ status: e.target.value })}
            className="h-12"
          >
            {Object.values(ResourceStatus).map((value) => (
              <option key={value} value={value}>
                {t(`spaces.resourceStatus.${value}`)}
              </option>
            ))}
          </Select>
        </Field>

        {/* Attaching, in the second of the two places the venue owner named:
            "when I add the resource I choose the price list". The same control
            serves the edit path, so a console created before there were any
            cards is fixed here rather than anywhere else. */}
        <div className="sm:col-span-2 space-y-2">
          <PricingListSelect
            value={form.pricingListId}
            onChange={(pricingListId) => set({ pricingListId })}
            onNavigateAway={onClose}
          />
        </div>

        <div className="sm:col-span-2">
          <Field label={t('spaces.resourceForm.state')}>
            <label className="flex min-h-12 items-center gap-3">
              <input
                type="checkbox"
                className="size-6"
                checked={form.isActive}
                onChange={(e) => set({ isActive: e.target.checked })}
              />
              <span>{t('spaces.resourceForm.activeHint')}</span>
            </label>
          </Field>
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-4 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      {refusal && (
        <div className="mt-4">
          <RefusalNotice refusal={refusal} names={{ resource: form.name }} tone="danger" />
        </div>
      )}

      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" size="lg" onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button size="lg" onClick={() => void save()} disabled={saving}>
          {saving ? t('settings.common.saving') : t('common.save')}
        </Button>
      </div>
    </Dialog>
  );
}
